import { theme as antdTheme } from 'antd';

// Modern (shadcn-like) antd theme with light and dark variants. Applied app-wide from
// App.jsx. Keep colors in sync with the CSS variables in assets/css/modern.css.
const FONT_FAMILY =
  "'Inter Tight', Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

const sharedTokens = {
  fontFamily: FONT_FAMILY,
  colorSuccess: '#10b981',
  colorWarning: '#f59e0b',
  colorError: '#e11d48',
  borderRadius: 8,
  borderRadiusLG: 12,
  borderRadiusSM: 6,
  controlHeight: 36,
  fontSize: 16
};

const palettes = {
  light: {
    token: {
      colorPrimary: '#0284c7',
      colorLink: '#0284c7',
      colorInfo: '#0284c7',
      colorText: '#0f172a',
      colorTextSecondary: '#64748b',
      colorTextTertiary: '#94a3b8',
      colorTextPlaceholder: '#94a3b8',
      colorBorder: '#e1e5eb',
      colorBorderSecondary: '#eceff3',
      colorSplit: '#eceff3',
      colorBgLayout: '#f6f7f9',
      colorBgContainer: '#ffffff',
      colorBgElevated: '#ffffff',
      colorFillAlter: '#fafbfc',
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.06)',
      boxShadowSecondary:
        '0 12px 32px -12px rgba(15, 23, 42, 0.18), 0 2px 6px rgba(15, 23, 42, 0.06)',
      boxShadowTertiary: '0 1px 2px rgba(15, 23, 42, 0.05)'
    },
    table: {
      headerBg: '#e7ecf2',
      headerColor: '#334155',
      headerSortActiveBg: '#d9e2ec',
      headerSortHoverBg: '#e0e8f0',
      rowHoverBg: '#f7f8fa',
      rowSelectedBg: '#edf6fb',
      rowSelectedHoverBg: '#e1f1fa',
      borderColor: '#eceff3',
      footerBg: '#fafbfc'
    },
    tagBg: '#f2f4f7',
    tagColor: '#334155',
    paginationActiveBg: '#edf6fb'
  },
  dark: {
    token: {
      colorPrimary: '#0ea5e9',
      colorLink: '#38bdf8',
      colorInfo: '#0ea5e9',
      colorText: '#e2e8f0',
      colorTextSecondary: '#94a3b8',
      colorTextTertiary: '#64748b',
      colorTextPlaceholder: '#64748b',
      colorBorder: '#26324d',
      colorBorderSecondary: '#1c2742',
      colorSplit: '#1c2742',
      colorBgLayout: '#0b1120',
      colorBgContainer: '#111a2e',
      colorBgElevated: '#162036',
      colorFillAlter: '#141e33',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
      boxShadowSecondary: '0 16px 40px -12px rgba(0, 0, 0, 0.6), 0 2px 6px rgba(0, 0, 0, 0.3)',
      boxShadowTertiary: '0 1px 2px rgba(0, 0, 0, 0.3)'
    },
    table: {
      headerBg: '#17223a',
      headerColor: '#cbd5e1',
      headerSortActiveBg: '#1d2a45',
      headerSortHoverBg: '#1d2a45',
      rowHoverBg: '#152037',
      rowSelectedBg: '#112d48',
      rowSelectedHoverBg: '#103957',
      borderColor: '#1c2742',
      footerBg: '#141e33'
    },
    tagBg: '#1a2540',
    tagColor: '#cbd5e1',
    paginationActiveBg: 'rgba(14, 165, 233, 0.16)'
  }
};

export const getAntdTheme = (mode = 'light') => {
  const p = palettes[mode] || palettes.light;
  return {
    algorithm: mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: { ...sharedTokens, ...p.token },
    components: {
      Card: {
        headerFontSize: 15,
        headerHeight: 52,
        paddingLG: 20
      },
      Button: {
        fontWeight: 500,
        defaultShadow: 'none',
        primaryShadow: mode === 'dark' ? 'none' : '0 1px 2px rgba(2, 132, 199, 0.25)',
        dangerShadow: 'none'
      },
      Table: {
        ...p.table,
        headerSplitColor: 'transparent',
        headerBorderRadius: 10,
        cellPaddingBlock: 12
      },
      Tabs: {
        inkBarColor: p.token.colorPrimary,
        itemSelectedColor: p.token.colorPrimary,
        itemHoverColor: p.token.colorLink,
        titleFontSize: 16
      },
      Modal: {
        titleFontSize: 17,
        borderRadiusLG: 14
      },
      Tag: {
        defaultBg: p.tagBg,
        defaultColor: p.tagColor
      },
      Pagination: {
        itemActiveBg: p.paginationActiveBg
      },
      Menu: {
        itemBorderRadius: 8
      }
    }
  };
};

// Backwards-compatible light theme export
export const modernAntdTheme = getAntdTheme('light');
